import { useState, type CSSProperties } from "react";
import {
  ChevronRight,
  CircleHelp,
  GraduationCap,
  Mail,
  UserRound,
} from "lucide-react";
import { Link } from "react-router";

import { BrandMark } from "@/components/BrandMark";
import { SignInForm } from "@/components/auth/SignInForm";
import { PUBLIC_LINK } from "@/components/public/control-classes";
import type { AuthGatewayContent } from "@/content/landing";
import { LEGAL_ROUTES } from "@/content/legal";
import { cn } from "@/lib/utils";

type GatewayStage = "current" | "invited";

interface AuthGatewayProps {
  authTransition: "legacy" | "tenant-internal";
  brandName: string;
  content: AuthGatewayContent;
  logoUrl: string | null;
}

interface StageButtonProps {
  active: boolean;
  description: string;
  detail?: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tone: "primary" | "highlight";
}

function StageButton({
  active,
  description,
  detail,
  icon,
  label,
  onClick,
  tone,
}: StageButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-controls="auth-gateway-panel"
      className={cn(
        "group grid min-h-[106px] w-full grid-cols-[56px_minmax(0,1fr)_32px] items-center gap-4 border-y border-transparent px-3 py-5 text-left transition-[border-color,background-color,box-shadow] duration-[var(--motion-fast)] ease-[var(--ease-standard)] focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-5 md:min-h-[88px] md:grid-cols-[44px_minmax(0,1fr)_24px] md:gap-3 md:px-4 md:py-3",
        active && "rounded-control border-[var(--public-primary-deep)] bg-[var(--auth-field)] shadow-[inset_7px_0_0_var(--public-primary-deep)]",
      )}
    >
      <span
        className={cn(
          "grid size-14 place-items-center rounded-full text-primary-foreground md:size-11 md:[&_svg]:size-6",
          tone === "primary" ? "bg-[var(--public-primary-deep)]" : "bg-highlight",
        )}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[18px] font-semibold leading-tight text-foreground sm:text-xl md:text-lg">
          {label}
        </span>
        <span className="mt-1 block text-base leading-snug text-foreground-soft sm:text-lg md:text-base">
          {description}
        </span>
        {detail && (
          <span className="mt-2 block text-sm leading-snug text-muted-foreground sm:text-base md:mt-1 md:text-sm">
            {detail}
          </span>
        )}
      </span>
      <ChevronRight
        className={cn("size-7 md:size-5", tone === "highlight" ? "text-highlight" : "text-[var(--public-primary-deep)]")}
        aria-hidden="true"
      />
    </button>
  );
}

export function AuthGateway({ authTransition, brandName, content, logoUrl }: AuthGatewayProps) {
  const [stage, setStage] = useState<GatewayStage>("current");
  const returnToSignIn = () => {
    if (stage !== "invited") return;
    setStage("current");
  };

  return (
    <main
      data-auth-page
      data-auth-surface="gateway"
      style={{ "--input": "var(--auth-border)", "--card": "var(--auth-field)" } as CSSProperties}
      className="min-h-screen bg-[var(--public-primary-deep)] bg-cover bg-center px-4 py-6 text-foreground sm:px-8 sm:py-10 md:grid md:place-items-center md:px-8 md:py-8 lg:px-10"
    >
      <section
        aria-labelledby="auth-portal-title"
        className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-[1100px] flex-col rounded-[14px] border border-[var(--auth-border)] bg-[var(--auth-surface)] shadow-[var(--elevation-popover)] sm:min-h-[calc(100vh-5rem)] md:min-h-0"
      >
        <header className="mx-6 flex min-h-[148px] flex-wrap items-center gap-x-7 gap-y-3 border-b border-border py-7 sm:mx-12 md:mx-8 md:min-h-[118px] md:gap-x-5 md:py-5 lg:mx-10">
          <BrandMark name={brandName} logoUrl={logoUrl} className="size-[78px] sm:size-[90px] md:size-[72px]" />
          <h1
            id="auth-portal-title"
            className="font-display text-[clamp(2rem,4vw,3.3rem)] font-semibold leading-none text-[var(--public-primary-deep)] md:text-[2.4rem] lg:text-[2.75rem]"
          >
            {brandName}
          </h1>
          <span className="hidden h-16 w-px bg-border sm:block md:h-12" aria-hidden="true" />
          <span
            data-auth-portal-label
            className="basis-full font-display text-2xl font-semibold text-highlight sm:basis-auto sm:text-4xl md:text-3xl"
          >
            Portal
          </span>
        </header>

        <div className="grid flex-1 md:grid-cols-2">
          <nav
            aria-labelledby="gateway-stage-heading"
            className="border-b border-border px-6 py-9 sm:px-12 md:border-b-0 md:border-r md:px-7 md:py-7 lg:px-10"
          >
            <h2
              id="gateway-stage-heading"
              className="mb-6 font-display text-[clamp(1.9rem,3vw,2.45rem)] font-semibold text-[var(--public-primary-deep)] md:mb-4 md:text-[1.7rem] lg:text-[2rem]"
            >
              What do you need to do?
            </h2>

            <div className="divide-y divide-border">
              <StageButton
                active={stage === "current"}
                label={content.currentAudience}
                description={content.currentDescription}
                icon={<UserRound className="size-8" />}
                onClick={returnToSignIn}
                tone="primary"
              />
              <StageButton
                active={stage === "invited"}
                label="Invited students"
                description="Activate approved access"
                detail="Use the secure link in your invitation email."
                icon={<Mail className="size-7" />}
                onClick={() => setStage("invited")}
                tone="highlight"
              />
              <a
                href={content.admissionsUrl}
                className="group grid min-h-[106px] grid-cols-[56px_minmax(0,1fr)_32px] items-center gap-4 px-3 py-5 text-left transition-colors hover:bg-well focus-visible:z-10 focus-visible:rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-5 md:min-h-[88px] md:grid-cols-[44px_minmax(0,1fr)_24px] md:gap-3 md:px-4 md:py-3"
              >
                <span
                  className="grid size-14 place-items-center rounded-full bg-[var(--public-foreground-soft)] text-primary-foreground md:size-11 md:[&_svg]:size-6"
                  aria-hidden="true"
                >
                  <GraduationCap className="size-8" />
                </span>
                <span>
                  <span className="block text-[18px] font-semibold leading-tight text-foreground sm:text-xl md:text-lg">
                    {content.prospectiveAudience}
                  </span>
                  <span className="mt-1 block text-base leading-snug text-foreground-soft sm:text-lg md:text-base">
                    {content.prospectiveDescription}
                  </span>
                </span>
                <ChevronRight className="size-7 text-[var(--public-foreground-soft)] md:size-5" aria-hidden="true" />
              </a>
            </div>
          </nav>

          <section
            id="auth-gateway-panel"
            className="flex items-start px-6 py-10 sm:px-12 md:px-7 md:py-7 lg:px-11"
          >
            <div className="w-full">
              {stage === "current" ? (
                <>
                  <div className="mb-8 flex items-center gap-5 md:mb-5 md:gap-3">
                    <span className="grid size-14 place-items-center rounded-full bg-[var(--public-primary-deep)] text-primary-foreground md:size-11" aria-hidden="true">
                      <UserRound className="size-8 md:size-6" />
                    </span>
                    <h2 className="font-display text-[clamp(2rem,3vw,2.65rem)] font-semibold text-[var(--public-primary-deep)] md:text-[1.75rem] lg:text-[2rem]">
                      Sign in
                    </h2>
                  </div>
                  <SignInForm surface="gateway" transition={authTransition} />
                </>
              ) : (
                <div className="max-w-[31rem]">
                  <div className="mb-8 flex items-center gap-5 md:mb-5 md:gap-3">
                    <span className="grid size-14 place-items-center rounded-full bg-highlight text-primary-foreground md:size-11" aria-hidden="true">
                      <Mail className="size-7 md:size-6" />
                    </span>
                    <h2 className="font-display text-[clamp(2rem,3vw,2.65rem)] font-semibold text-[var(--public-primary-deep)] md:text-[1.75rem] lg:text-[2rem]">
                      Activate access
                    </h2>
                  </div>
                  <p className="font-display text-2xl leading-relaxed text-foreground md:text-xl">
                    {authTransition === "tenant-internal"
                      ? "Use the secure activation link in your invitation email, then continue with the managed Google account created for you."
                      : "Use the secure activation link in your invitation email, then choose the password for your account."}
                  </p>
                  <p className="mt-6 text-base leading-relaxed text-muted-foreground md:mt-4">
                    Portal access is issued after approval. There is no public account registration form.
                  </p>
                  <button
                    type="button"
                    onClick={returnToSignIn}
                    className={`${PUBLIC_LINK} mt-9 text-ui font-semibold text-primary hover:underline md:mt-6`}
                  >
                    Already activated? Sign in
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>

        <footer className="mx-6 flex min-h-[120px] flex-col items-center justify-center gap-4 border-t border-border px-4 py-8 sm:mx-12 md:mx-8 md:min-h-[92px] md:gap-1 md:py-2 lg:mx-10">
          <a
            href={content.supportUrl}
            className="inline-flex min-h-11 items-center gap-3 font-semibold text-[var(--public-readable-highlight)] underline-offset-4 hover:underline focus-visible:rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CircleHelp className="size-8 md:size-6" aria-hidden="true" />
            <span className="text-lg md:text-base">Need help?</span>
          </a>
          {/* Someone deciding whether to sign in or apply looks for these here,
              and Google requires the privacy policy to be reachable. */}
          <nav aria-label="Legal" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link
              to={LEGAL_ROUTES.privacy}
              className="inline-flex min-h-[var(--control-height-compact)] items-center font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground underline-offset-4 transition-colors hover:text-[var(--public-primary)] hover:underline focus-visible:rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Privacy policy
            </Link>
            <Link
              to={LEGAL_ROUTES.terms}
              className="inline-flex min-h-[var(--control-height-compact)] items-center font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground underline-offset-4 transition-colors hover:text-[var(--public-primary)] hover:underline focus-visible:rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Terms of service
            </Link>
          </nav>
        </footer>
      </section>
    </main>
  );
}
